// src/components/layout/AppShell.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { pageKeys } from '../../utils/pageAccess'; // [marker: permissions-real v1]
import Sidebar from './Sidebar';
import BackButton from './BackButton'; // [marker: back-tags v1]
import AssistantPanel from '../AssistantPanel'; // [marker: ai-assistant v1]
import api from '../../utils/api';
import { EventChecklistReminder } from '../EventChecklist'; // [marker: event-checklist v1]
import RenewalReminder from '../RenewalReminder'; // [marker: renewals-tracker v1]

function TaskReminder() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const check = async () => {
    try {
      const res = await api.get('/tasks/notifications/my');
      const list = (res.data || []).filter(n => !n.is_read);
      if (list.length) { setItems(list); setOpen(true); }
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const dismiss = async () => {
    setOpen(false);
    try { await api.post('/tasks/notifications/mark-all-read'); } catch (e) {}
  };
  if (!open || !items.length) return null;
  return (
    <div className="modal-overlay" onClick={dismiss} style={{ zIndex: 1000 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">🔔 Task reminders</span>
          <button className="btn btn-ghost btn-icon" onClick={dismiss}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 10 }}>
          {items.slice(0, 8).map(n => (
            <div key={n.id} style={{ borderLeft: '3px solid var(--navy)', paddingLeft: 10 }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{n.message || n.task_title || 'Task'}</div>
              {n.task_title && n.message && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{n.task_title}</div>}
            </div>
          ))}
          {items.length > 8 && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>+{items.length - 8} more…</div>}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={dismiss}>Dismiss</button>
          <button className="btn btn-primary" onClick={() => { setOpen(false); navigate('/tasks'); }}>View Tasks</button>
        </div>
      </div>
    </div>
  );
}

// [marker: permissions-real v1] a page the person isn't allowed to open shows this instead
function NoAccess() {
  return (
    <div className="card" style={{ maxWidth: 560, margin: '60px auto', padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: '2rem' }}>🔒</div>
      <h2 style={{ margin: '8px 0' }}>You don’t have access to this page</h2>
      <p style={{ color: 'var(--gray-600)' }}>Ask a super admin to change your permissions if you need it.</p>
    </div>
  );
}

export default function AppShell() {
  const { can, permsLoaded } = useAuth();
  const location = useLocation();
  const keys = pageKeys(location.pathname);
  const allowed = !keys || can(keys);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim() || search.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(search)}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const navigateToResult = (type, id) => {
    const paths = {
      business: `/members/${id}`,
      contact:  `/contacts/${id}`, // [marker: back-tags v1] open the contact itself
      invoice:  `/invoices/${id}`,
      event:    `/events/${id}`,
      ticket:   `/events`,
    };
    navigate(paths[type] || '/');
    setSearchResults(null);
    setSearch('');
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <TaskReminder />
      <EventChecklistReminder /> {/* [marker: event-checklist v1] */}
      <RenewalReminder /> {/* [marker: renewals-tracker v1] */}
      <div className="main-area">
        <header className="topbar">
          <BackButton style={{ marginRight: 8 }} />
          <form onSubmit={handleSearch} style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <div className="search-bar">
              <span>🔍</span>
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); if (!e.target.value) setSearchResults(null); }}
                placeholder="Search members, invoices, events..."
              />
              {searching && <span className="spinner" style={{ width: 14, height: 14 }} />}
            </div>

            {searchResults && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
                border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-lg)', zIndex: 999, marginTop: 4, maxHeight: 400, overflowY: 'auto'
              }}>
                {searchResults.total === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.875rem' }}>No results found</div>
                ) : (
                  <>
                    {searchResults.businesses?.map(b => (
                      <div key={b.id} onClick={() => navigateToResult('business', b.id)}
                        style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: 8, alignItems: 'center' }}
                        onMouseOver={e => e.currentTarget.style.background = 'var(--gray-50)'}
                        onMouseOut={e => e.currentTarget.style.background = 'white'}>
                        <span style={{ fontSize: '0.75rem', background: 'var(--navy-ghost)', padding: '1px 6px', borderRadius: 4, color: 'var(--navy)', fontWeight: 600 }}>BIZ</span>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{b.name}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginLeft: 'auto' }}>{b.status}</span>
                      </div>
                    ))}
                    {searchResults.contacts?.map(c => (
                      <div key={c.id} onClick={() => navigateToResult('contact', c.id)}
                        style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: 8, alignItems: 'center' }}
                        onMouseOver={e => e.currentTarget.style.background = 'var(--gray-50)'}
                        onMouseOut={e => e.currentTarget.style.background = 'white'}>
                        <span style={{ fontSize: '0.75rem', background: '#f0fdf4', padding: '1px 6px', borderRadius: 4, color: 'var(--success)', fontWeight: 600 }}>CON</span>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{c.name}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{c.business_name}</span>
                      </div>
                    ))}
                    {searchResults.invoices?.map(inv => (
                      <div key={inv.id} onClick={() => navigateToResult('invoice', inv.id)}
                        style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: 8, alignItems: 'center' }}
                        onMouseOver={e => e.currentTarget.style.background = 'var(--gray-50)'}
                        onMouseOut={e => e.currentTarget.style.background = 'white'}>
                        <span style={{ fontSize: '0.75rem', background: '#eff6ff', padding: '1px 6px', borderRadius: 4, color: 'var(--info)', fontWeight: 600 }}>INV</span>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{inv.name}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{inv.business_name}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </form>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
          </div>
        </header>

        <main className="page-content">
          {!permsLoaded && keys ? <div className="spinner" /> : allowed ? <Outlet /> : <NoAccess />}
        </main>
      </div>
      <AssistantPanel />
    </div>
  );
}
